import {defineStore} from 'pinia';
import {useContextStore} from './contextStore';
import {useProgressStore} from './other/progressStore';
import {useWebSocketFactoryCacheStore} from '@/stores/global/webSocketFactoryCacheStore';
import commonFunction from '@/commons/commonFunction';
import WebSocketEvent from '@/commons/wildcards/constants/WebSocketEvent';
import fileApi from '@/apis/fl/fileApi';
import StorageFileType from '@/commons/wildcards/constants/StorageFileType';
import exportApi from '@/apis/ex/exportApi';
import {MSConstant} from '@/commons/wildcards';
import {msFormat} from '@/commons/msFormat';
import commonFn from '@/commons/commonFunction';
import popupUtil from '@/commons/popupUtil.js';
import {useDataManagementStore} from '@/stores/dataManagement/dataManagementStore';
import EventBusGlobal, {GlobalEventName} from '@/commons/eventBusGlobal';
import {showAlert} from '@/commons/globalMessage';
import {useWebSocketFactoryNotificationStore} from '@/stores/global/webSocketFactoryNotificationStore';
import {useToast} from 'vue-toast-notification';
// api
import {$vfm} from 'vue-final-modal';

export const useGlobalSocketStore = defineStore('GlobalSocket', {
  state: () => {
    return {mapSession: {}, mapTimeInterval: {}, connectedSocket: false, sessionId: null};
  },
  actions: {
    updateConnectedSocket(sessionId) {
      this.connectedSocket = true;
      this.sessionId = sessionId;
    },
    [WebSocketEvent.InvalidCacheDi]: args => {
      const webSocketData = JSON.parse(args);
      if (webSocketData) {
        const messageData = webSocketData.Data;
        if (messageData) {
          const context = useContextStore();
          if (context.context.PandaSessionId && messageData.client && messageData.module) {
            const store = useWebSocketFactoryCacheStore().factoryCacheStore(messageData.module);
            if (store) {
              store.loadFullCache({remoteVersion: messageData.version});
            }
          }
        }
      }
    },

    [WebSocketEvent.CloseLedgerCompleted]: async function (args) {
      const msg = JSON.parse(args);
      const errors = msg.Data;
      const progressStore = useProgressStore();
      progressStore.setProgress({key: MSConstant.ProgressKey.CloseLedger, isCompleted: true});

      if (errors && errors.length > 0) {
        popupUtil.show('ImpossiblePostedVouchersPopup', errors);
      } else {
        useToast().success('Khóa sổ thành công', {position: 'top'});
      }
    },

    [WebSocketEvent.PostBatchProcess]: async function (args) {
      const message = JSON.parse(args);

      if (message) {
        usePostedBatch().setMessage(message);
      }
    },

    // socket cho ghi sổ nhiều chứng từ
    [WebSocketEvent.PostMultiCompleted]: async function (args) {
      // Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.postMultiCompleted);
    },

    [WebSocketEvent.ExportList]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportList] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportList];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      // Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    /**
     * Xuất khẩu XML theo websocket
     * HQMINH 05.09.2023
     */
    [WebSocketEvent.ExportXML]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportXML] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportXML];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      // Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    /**
     * Xuất khẩu Json theo websocket
     * HQMINH 27.09.2023
     */
    [WebSocketEvent.ExportJson]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (this.mapSession[WebSocketEvent.ExportJson] !== ActionId) return;
      const intervalId = this.mapTimeInterval[WebSocketEvent.ExportJson];
      clearInterval(intervalId);

      const link = fileApi.getFilePreviewLink(StorageFileType.Temp, `${Data.fileName}`, Data.name);
      commonFunction.downloadLink(link);

      // Thêm event bus ẩn toast
      EventBusGlobal.$emit(GlobalEventName.clearToast);
    },

    // Cập nhật layout template
    [WebSocketEvent.UpdateLayoutTemplate]: function (args) {
      const {Data, ActionId} = JSON.parse(args);
      if (Data && Data.length > 0) {
        const context = useContextStore();
        const userID = context?.context?.Context?.UserId;
        if (userID) {
          Data.forEach(x => {
            commonFn.removeSessionStorage('LayoutTemplate_{0}_{1}'.format(x, userID));
          });
        }
      }
    },

    /**
     * Admin đã xóa người dùng === chính bạn -> logout
     * @param {*} args
     * nvxuan 04.10.2023
     */
    [WebSocketEvent.DeleteSysUser]: function (args) {
      const {user_id} = JSON.parse(args);
      const context = useContextStore();
      const curUserID = context?.context?.Context?.UserId;

      if (curUserID && curUserID == user_id) {
        commonFn.logoff();
      }
    },

    /**
     * Admin đã thay đổi vai trò của bạn, bạn cần load lại vai trò
     * @param {*} args
     * nvxuan 04.10.2023
     */
    [WebSocketEvent.ChangeRoleOfSysUser]: async function (args) {
      const {user_id, exe_user_id, affectUserIds} = JSON.parse(args);
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const payload = {
        databaseId: context?.CurDatabaseId,
        tenantId: context?.context?.Context?.TenantId,
        userId: curUserID,
      };

      if ((user_id && curUserID == user_id) || (affectUserIds && affectUserIds.contains(curUserID))) {
        const token = await context.getToken(payload);

        // Nếu chính mình thay đổi vai trò của mình thì reload luôn không cần thông báo
        if (exe_user_id == curUserID) {
          location.reload();
        } else {
          // show thông báo
          showAlert('Quản trị dữ liệu đã thay đổi vai trò quyền hạn của bạn.', 'Quyền hạn thay đổi').then(() => {
            location.reload();
          });
        }
      }
    },

    /**
     * Cập nhật quyền hạn realtime khi Admin thay đổi permission của role
     * Giống MIMOSA: websocket-global-update-permission
     * @param {*} args
     */
    [WebSocketEvent.UpdatePermission]: async function (args) {
      const res = JSON.parse(args);
      const data = res?.Data;
      if (data && data.listUserId) {
        const context = useContextStore();
        const curUserID = context?.CurUserId;

        // Kiểm tra user hiện tại có nằm trong danh sách cần cập nhật không
        if (data.listUserId.includes(curUserID)) {
          console.log('Bạn vừa có quyền mới');

          // Gọi action cập nhật lại permission từ server
          await context.updatePermission(curUserID);

          // Có thể thêm toast thông báo cho user (optional)
          // useToast().info('Quyền hạn của bạn đã được cập nhật', {position: 'top'});
        }
      }
    },

    /**
     * Thông báo bắn về
     * @param {*} sender
     * @param {*} res
     * tvtoan 2.6.2023
     */
    notification: args => {
      const messageData = JSON.parse(args);
      // 5: khóa sổ
      if (messageData.type == 5) {
        const errorDataCloseBook = useWebSocketFactoryNotificationStore().convertData(messageData);
      }
    },

    [WebSocketEvent.NewNotification]: function (args) {
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const notiData = JSON.parse(args)?.Data;
      const {affect_user_id, target} = notiData;
      let isValid = false;

      // check đối tượng nhận thông báo
      // target
      //    0.All.Tất cả
      //    1.User.Thông báo cho user
      //    2.UserWithDB.Thông báo cho user của một database
      //    3.Database.Thông báo cho tất cả người dùng trong database
      if ([0, 3].includes(target) || ([1, 2].includes(target) && affect_user_id == curUserID)) {
        isValid = true;
      }

      if (isValid) {
        // Lấy lại số lượng thông báo chưa đọc hiển thị trên TopNav
        commonFn.refreshNotiBell();
      }
    },

    /**
     * Tự động cập nhật lại dữ liệu danh sách hồ sơ khi có sự thay đổi sau khi lấy danh sách từ DVC về
     * @param {*} args
     * nvthinh 9.11.2023
     */
    [WebSocketEvent.UpdateDvcList]: function (args) {
      // Thêm event bus reload màn DVC
      EventBusGlobal.$emit(GlobalEventName.gridReloadDVC);
    },

    /**
     * Emit sự kiện lấy kết quả sau khi thực hiện sinh CT từ CT salagov
     * @param {Object} response
     * nvthinh 22.1.2023
     */
    [WebSocketEvent.GenerateVoucherFromSalagov]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.generateVoucherFromSalagov, response);
    },

    /**
     * Emit sự kiện lấy kết quả sau khi thực hiện Kết chuyển nhanh
     * @param {Object} response
     * nvthinh 29.01.2023
     */
    [WebSocketEvent.QuickTransfer]: function (response) {
      EventBusGlobal.$emit(GlobalEventName.quickTransfer, response);
    },

    /**
     * Thông báo bắn về
     * @param {*} sender
     * @param {*} res
     * tvtoan 2.6.2023
     */
    converting: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      const date = new Date(message.CreatedDate).getDate();
      const month = new Date(message.CreatedDate).getMonth() + 1;
      const monthfixed = month < 10 ? '0' + month : month;
      const year = new Date(message.CreatedDate).getFullYear();
      message.createdDate = `${date}/${monthfixed}/${year}`;
      useDataManagementStore().setMessage(message);
    },

    endConvert: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      const date = new Date(message.CreatedDate).getDate();
      const month = new Date(message.CreatedDate).getMonth() + 1;
      const monthfixed = month < 10 ? '0' + month : month;
      const year = new Date(message.CreatedDate).getFullYear();
      message.createdDate = `${date}/${monthfixed}/${year}`;
      useDataManagementStore().setMessage(message);
    },

    /**
     * Cập nhật % tiến trình sao lưu dữ liệu (realtime từ worker qua WebSocket)
     */
    [WebSocketEvent.BackupDataProgress]: args => {
      const data = JSON.parse(args)?.Data;
      if (data?.backup_database_id != null) {
        EventBusGlobal.$emit(GlobalEventName.backupDataProgress, {
          backup_database_id: data.backup_database_id,
          current_percent: data.current_percent,
        });
      }
    },

    /**
     * Cập nhật % tiến trình phục hồi dữ liệu (realtime từ worker qua WebSocket)
     */
    [WebSocketEvent.RestoreDataProgress]: args => {
      const data = JSON.parse(args)?.Data;
      // BE (ResourceWorker) sends restore_database_id; old flow sends restore_id — normalise to restore_id
      const restoreId = data?.restore_database_id ?? data?.restore_id;
      if (restoreId != null) {
        EventBusGlobal.$emit(GlobalEventName.restoreDataProgress, {
          restore_id: restoreId,
          current_percent: data.current_percent,
        });
      }
    },

    /**
     * Bắt đầu sao lưu dữ liệu
     * nvxuan 20.11.2023
     * Backup send by dbid
     */
    [WebSocketEvent.BackupDataStart]: args => {
      const messageData = JSON.parse(args)?.Data;
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const keyFlag = '_ShowBackupProcesss';

      messageData?.BackupParam &&
        messageData?.BackupDataRecord &&
        !window[keyFlag] &&
        popupUtil.show('BackupRestoreProcessPopupX', {
          BackupDataRecord: messageData.BackupDataRecord,
        });

      delete window[keyFlag];
    },

    /**
     * Hủy sao lưu dữ liệu
     * nvxuan 20.11.2023
     */
    [WebSocketEvent.BackupDataCancel]: args => {
      const messageData = JSON.parse(args);

      // Hủy sao lưu dữ liệu
      EventBusGlobal.$emit(GlobalEventName.backupDataCancel, {
        backup_database_id: messageData.Data.BackupDataRecord.backup_database_id,
      });

      // reload lại ds Quản lý dữ liệu
      EventBusGlobal.$emit(GlobalEventName.reloadListDbManagement);

      // reload lại danh sách sao lưu & khôi phục nếu đang mở
      EventBusGlobal.$emit(GlobalEventName.reloadListBackupRestore);
    },

    /**
     * Sau khi backup dữ liệu kết thúc
     * nvxuan 20.11.2023
     */
    [WebSocketEvent.BackupDataCompleted]: async args => {
      const messageData = JSON.parse(args).Data;
      console.log('[BackupDataCompleted] raw args:', args);
      console.log('[BackupDataCompleted] messageData:', messageData);

      // Refresh chuông thông báo khi sao lưu thành công
      if (messageData?.Success) {
        commonFn.refreshNotiBell();
      }

      // Kết thúc backup
      messageData && EventBusGlobal.$emit(GlobalEventName.backupDataCompleted, messageData);
    },

    /**
     * Sau khi khôi phục dữ liệu kết thúc
     * nvxuan 22.11.2023
     */
    [WebSocketEvent.RestoreDataCompleted]: args => {
      const parsed = JSON.parse(args);
      const messageData = parsed.Data;

      if (messageData?.Success) {
        commonFn.refreshNotiBell();
      }

      // Do người dùng thực hiện ở 1 db nhưng trong quá trình chuyển sang DB khác nên cần insert thông báo ở db đang đứng

      // Kết thúc backup
      if (messageData) {
        messageData.ErrorMessage = parsed.ErrorMessage || parsed.Message || '';
        EventBusGlobal.$emit(GlobalEventName.restoreDataCompleted, messageData);
      }
    },

    /**
     * Bắt đầu bảo trì dữ liệu
     * nvxuan 08.12.2023
     */
    [WebSocketEvent.DataMaintainStart]: args => {
      const messageData = JSON.parse(args).Data;
      const context = useContextStore();
      const curUserID = context?.CurUserId;
      const curDbID = context?.CurDatabaseId;
      const taskRecord = messageData?.TaskManagementParam;
      const {openedModals} = $vfm;
      const isOpen =
        Array.isArray(openedModals) &&
        openedModals.length > 0 &&
        openedModals.filter(e => e?.params?._rawValue?.name == 'DataMantainance').length > 0;

      taskRecord &&
        taskRecord.database_id == curDbID &&
        (taskRecord.user_id != curUserID || !isOpen) &&
        popupUtil.show('DataMantainance', {
          callFrom: 'MainLayout',
          TaskManagementParam: taskRecord,
        });
    },

    /**
     * tạo dữ liệu mới từ dữ liệu cũ
     * ntduong2 8.11.2024
     */
    [WebSocketEvent.CreateDataPrevYear]: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      useDataManagementStore().setMessage(message);
    },

    /**
     * tiến độ tách dữ liệu (event chung)
     */
    [WebSocketEvent.UpdateCircular]: args => {
      const messageData = JSON.parse(args);
      const message = JSON.parse(messageData.Message);
      // ErrorMessage nằm ở top-level messageData (không nằm trong Message JSON)
      message.ErrorMessage = messageData.ErrorMessage ?? '';
      // Tag source = 'circular' để DataManagementList phân biệt với convert Panda.NET
      // (cùng dùng dataManagementStore) — tránh duplicate vào convertingDatabases
      message.Source = 'circular';
      useDataManagementStore().setMessage(message);
    },

    // #region Thông báo kết quả trả về chức năng nộp báo cáo
    [WebSocketEvent.CreateSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.SubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.CreateAndSubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập và nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    // #endregion

    // #region Thông báo kết quả trả về chức năng nộp báo cáo
    [WebSocketEvent.CreateSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.SubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.CreateAndSubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập và nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    // #endregion

    // #region Thông báo kết quả trả về chức năng nộp báo cáo
    [WebSocketEvent.CreateSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.SubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    [WebSocketEvent.CreateAndSubmitSummaryReport]: args => {
      const toast = useToast();
      toast.success('Lập và nộp báo cáo thành công', {position: 'top'});
      EventBusGlobal.$emit(GlobalEventName.refreshSubmitReportList);
      commonFn.unmask();
    },
    // #endregion

    /**
     * Xuất khẩu danh sách
     * @param {*} exportParam: Param xuất khẩu
     */
    async exportList(exportParam) {
      this.processData(exportParam.ColumnExports, exportParam.Data);
      const sessionId = await exportApi.exportListAsync(exportParam);
      this.mapSession[WebSocketEvent.ExportList] = sessionId;

      // Tên file tải về
      const fileDownName = exportParam && exportParam.FileName ? `${exportParam.FileName}.xlsx` : null;

      // Khởi tạo 1 check pull lên server
      this.mapTimeInterval[WebSocketEvent.ExportList] = commonFunction.createIntervalCheckPull(
        window._appConfig.totalCheckPull.exportList,
        window._appConfig.timeIntervalPull.exportList,
        `${sessionId}.xlsx`,
        fileDownName,
      );
    },

    /**
     * Process lại dữ liệu export
     * @param {*} columns
     * @param {*} data
     */
    processData(columns, data) {
      columns = columns.filter(x => x.dataField !== 'rowAction' && x.dataField !== 'rowState' && x.visible !== false);
      const colFormatEnums = columns.filter(x => x.formatType === MSConstant.FormatType.Enum);

      if (colFormatEnums.length > 0) {
        data.forEach(item => {
          colFormatEnums.forEach(col => {
            if (col.enum) {
              // sửa lỗi format enum nhhoang1 20.07.2023
              const text = msFormat.formatEnum(item[col.dataField], col.enum);

              item[`${col.dataField}_enum`] = text;
            }
          });
        });
      }
    },

    /**
     * Hủy bỏ Interval theo key
     * @param {String} key
     * @author nvthinh 28.11.2023
     */
    clearIntervalByKey(key) {
      const me = this;

      const intervalId = me.mapTimeInterval[key];
      clearInterval(intervalId);
      delete me.mapTimeInterval[key];
    },
  },
});
